import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type DefaultMessagesResponse,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useEffect, useState } from 'react';

export type PlacementFrontendProps = {
	edition: 'free' | 'standard' | 'premium' | 'unknown';
	shouldRenderIncidentsPremiumTrialNudge: boolean;
};

type CustomDismissCallback = () => void;

export type PlacementRenderProps = {
	dismiss: CustomDismissCallback;
} & PlacementFrontendProps;

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

const placementConfig: PlacementBrowserConfig = {
	placementId: 'jsm-issue-view-details-section',
	options: {
		choreographer: {
			isDefaultEnabled: false,
		},
	},
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	PlacementFrontendProps
> = createFrontend((Messages) => {
	return ({ edition, shouldRenderIncidentsPremiumTrialNudge }: PlacementFrontendProps) => {
		const [messages, setMessages] = useState<DefaultMessagesResponse['messages']>();

		const apiResponse = usePlacementApi<DefaultMessagesResponse>('/');

		useEffect(() => {
			const { error, data, isLoading } = apiResponse;

			if (isLoading) {
				return;
			}

			if (error) {
				throw error;
			}

			setMessages(data.messages);
		}, [apiResponse]);

		const handleDismiss = (messageInstanceId: string): void => {
			setMessages(() => {
				if (!messages) {
					return;
				}

				const filteredMessages = messages.filter(
					(message) => messageInstanceId !== message.messageInstanceId,
				);

				return filteredMessages;
			});
		};

		return !apiResponse.isLoading ? (
			<>
				{messages?.map((props) => (
					<Messages.render
						{...props}
						key={props.messageInstanceId}
						edition={edition}
						dismiss={() => handleDismiss(props.messageInstanceId)}
						shouldRenderIncidentsPremiumTrialNudge={shouldRenderIncidentsPremiumTrialNudge}
					/>
				))}
			</>
		) : null;
	};
});
